import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Shield, Heart, Plus, Minus, Crown, ChevronDown, Backpack } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageLoader, PageHeader, EmptyState } from "@/components/common";

const CONDITIONS = ["Blinded", "Charmed", "Frightened", "Grappled", "Paralyzed", "Poisoned", "Prone", "Restrained", "Stunned", "Unconscious"];

export default function DMView() {
  const [campId, setCampId] = useState(null);
  const { data: camps, isLoading } = useQuery({ queryKey: ["campaigns"], queryFn: async () => (await api.get("/campaigns")).data });

  if (isLoading) return <PageLoader />;
  const dmCamps = (camps || []).filter((c) => c.is_dm);
  const active = campId || dmCamps[0]?.id;

  return (
    <div>
      <PageHeader title="DM View" subtitle="Command your table — adjust HP, conditions and inventory" icon={Shield} />
      {dmCamps.length === 0 ? (
        <EmptyState icon={Crown} title="No campaigns to run" subtitle="Create a campaign as DM to manage your party here." />
      ) : (
        <>
          <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
            {dmCamps.map((c) => (
              <button key={c.id} onClick={() => setCampId(c.id)} data-testid={`dm-camp-${c.id}`} className={`px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition ${active === c.id ? "btn-violet" : "glass glass-hover text-zinc-400"}`}>
                {c.name}
              </button>
            ))}
          </div>
          {active && <DMParty campId={active} />}
        </>
      )}
    </div>
  );
}

function DMParty({ campId }) {
  const { data: camp, isLoading } = useQuery({ queryKey: ["campaign", campId], queryFn: async () => (await api.get(`/campaigns/${campId}`)).data });
  if (isLoading) return <PageLoader />;
  if (!camp?.characters?.length) return <EmptyState icon={Shield} title="Empty party" subtitle="Players need to add characters to this campaign." />;
  return (
    <div className="space-y-3">
      {camp.characters.map((c) => <DMCharRow key={c.id} char={c} campId={campId} />)}
    </div>
  );
}

function DMCharRow({ char, campId }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [amt, setAmt] = useState("");
  const hp = char.hp || { current: 0, max: 0, temp: 0 };
  const pct = Math.max(0, Math.min(100, (hp.current / (hp.max || 1)) * 100));

  const save = async (patch) => {
    try {
      await api.put(`/characters/${char.id}`, patch);
      qc.invalidateQueries({ queryKey: ["campaign", campId] });
    } catch { toast.error("Update failed (need DM rights)"); }
  };

  const applyHP = (sign) => {
    const v = parseInt(amt) || 0; if (!v) return;
    let cur = hp.current, temp = hp.temp || 0;
    if (sign < 0) { const ft = Math.min(temp, v); temp -= ft; cur = Math.max(0, cur - (v - ft)); }
    else cur = Math.min(hp.max, cur + v);
    save({ hp: { ...hp, current: cur, temp } });
    setAmt("");
  };

  const toggleCond = (cond) => {
    const has = char.conditions?.includes(cond);
    save({ conditions: has ? char.conditions.filter((x) => x !== cond) : [...(char.conditions || []), cond] });
  };

  return (
    <div className="glass rounded-2xl overflow-hidden" data-testid={`dm-char-${char.id}`}>
      <div className="p-4 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-violet-900 flex items-center justify-center font-display font-bold shrink-0">{char.name?.[0]?.toUpperCase()}</div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-white truncate">{char.name}</div>
          <div className="text-xs text-zinc-500">Lvl {char.level} {char.class} · AC {char.armor_class}</div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mt-2 max-w-[200px]">
            <div className="h-full bg-gradient-to-r from-rose-600 to-rose-400" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={() => applyHP(-1)} data-testid={`dm-dmg-${char.id}`} className="p-2 rounded-lg bg-rose-500/15 text-rose-300 hover:bg-rose-500/25"><Minus size={15} /></button>
          <input value={amt} onChange={(e) => setAmt(e.target.value)} type="number" placeholder="0" className="input-dark rounded-lg w-14 text-center text-sm py-1.5" data-testid={`dm-amt-${char.id}`} />
          <button onClick={() => applyHP(1)} data-testid={`dm-heal-${char.id}`} className="p-2 rounded-lg bg-teal-500/15 text-teal-300 hover:bg-teal-500/25"><Plus size={15} /></button>
          <div className="text-center px-2">
            <div className="flex items-center gap-1 text-rose-400 text-sm font-bold"><Heart size={13} /> {hp.current}/{hp.max}</div>
            {hp.temp > 0 && <div className="text-[10px] text-teal-400">+{hp.temp} temp</div>}
          </div>
          <button onClick={() => setOpen(!open)} className="p-2 rounded-lg glass-hover text-zinc-400"><ChevronDown size={16} className={`transition ${open ? "rotate-180" : ""}`} /></button>
        </div>
      </div>
      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-white/[0.05] pt-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Conditions</div>
            <div className="flex flex-wrap gap-2">
              {CONDITIONS.map((cond) => {
                const active = char.conditions?.includes(cond);
                return <button key={cond} onClick={() => toggleCond(cond)} data-testid={`dm-cond-${char.id}-${cond}`} className={`text-xs px-3 py-1.5 rounded-full border transition ${active ? "bg-rose-500/20 border-rose-500/50 text-rose-200" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}>{cond}</button>;
              })}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2 flex items-center gap-1.5"><Backpack size={13} /> Inventory ({char.inventory?.length || 0})</div>
            {char.inventory?.length ? (
              <div className="flex flex-wrap gap-2">
                {char.inventory.map((it) => <span key={it.id} className="text-xs bg-white/[0.04] border border-white/[0.06] px-2.5 py-1 rounded-lg text-zinc-300">{it.name} ×{it.quantity}</span>)}
              </div>
            ) : <p className="text-xs text-zinc-600">Empty</p>}
          </div>
        </div>
      )}
    </div>
  );
}
